import { useState, useMemo, useEffect } from "react";
import { Search, BookOpen, ChevronDown } from "lucide-react";
import Card from "../components/ui/Card";
import Pill from "../components/ui/Pill";
import { useAppState } from "../context/AppStateContext";

// Import wisdom data from separate files
import { namesOfAllah } from "../lib/wisdom/namesOfAllah";
import { quickBoosts } from "../lib/wisdom/quickBoosts";
import { knowledgeItems } from "../lib/wisdom/knowledgeItems";

// --- Main Component ---
export default function WisdomPage() {
	const { actions } = useAppState();
	
	// Daily Name of Allah logic
	const todayIdx = useMemo(() => new Date().getDate() % namesOfAllah.length, []);
	const dailyName = namesOfAllah[todayIdx];

	// Knowledge search/filter logic
	const [search, setSearch] = useState("");
	const [expanded, setExpanded] = useState(null);
	const [expandedBoost, setExpandedBoost] = useState(null);
	const [expandedName, setExpandedName] = useState(false);
	const [nameReadLogged, setNameReadLogged] = useState(false);
	
	// Initialize collapsed state from localStorage
	const [collapsedCategories, setCollapsedCategories] = useState(() => {
		try {
			const saved = localStorage.getItem("wisdomCollapsedCategories");
			return saved ? JSON.parse(saved) : {};
		} catch {
			return {};
		}
	});
	const [collapsedBoosts, setCollapsedBoosts] = useState(() => {
		try {
			const saved = localStorage.getItem("wisdomCollapsedBoosts");
			return saved !== null ? JSON.parse(saved) : false;
		} catch {
			return false;
		}
	});

	// Save collapsed categories to localStorage whenever they change
	useEffect(() => {
		try {
			localStorage.setItem("wisdomCollapsedCategories", JSON.stringify(collapsedCategories));
		} catch (error) {
			console.warn("Failed to save collapsed categories:", error);
		}
	}, [collapsedCategories]);

	// Save collapsed boosts to localStorage whenever it changes
	useEffect(() => {
		try {
			localStorage.setItem("wisdomCollapsedBoosts", JSON.stringify(collapsedBoosts));
		} catch (error) {
			console.warn("Failed to save collapsed boosts:", error);
		}
	}, [collapsedBoosts]);

	const filteredItems = useMemo(() => {
		if (!search) return knowledgeItems;
		const q = search.toLowerCase();

		return knowledgeItems.filter((item) => {
			const inTitle = item.title && item.title.toLowerCase().includes(q);
			const inContent = item.content && item.content.toLowerCase().includes(q);
			const inTranslation = item.translation && item.translation.toLowerCase().includes(q);
			const inSource = item.source && item.source.toLowerCase().includes(q);
			const inTag =
				(item.tag && item.tag.toLowerCase().includes(q)) ||
				(Array.isArray(item.tags) &&
					item.tags.some((tag) => tag && tag.toLowerCase().includes(q)));

			return inTitle || inContent || inTranslation || inSource || inTag;
		});
	}, [search]);

	// Group by category
	const categories = ["Dua", "Hadith", "Productivity Tips"];
	const grouped = categories.map((cat) => ({
		category: cat,
		items: filteredItems.filter((item) => item.category === cat),
	}));

	return (
		<div className="space-y-2 flex flex-col" style={{ paddingBottom: "calc(80px + env(safe-area-inset-bottom, 20px))" }}>
			{/* Daily Focus Card */}
			<Card className="bg-gradient-to-br from-emerald-950/20 to-[#18181B] border border-emerald-900/30">
				<div className="flex flex-col items-center py-4">
					<div className="text-xs uppercase tracking-widest text-[#10B981] font-semibold mb-2">
						Daily Name of Allah
					</div>
					<div className="text-5xl md:text-6xl font-arabic text-[#10B981] glow-mint mb-2">
						{dailyName.arabic}
					</div>
					<div className="text-lg font-bold text-white mb-1">
						{dailyName.transliteration}
					</div>
					<div className="text-sm text-zinc-400 mb-4 text-center max-w-xs">
						{dailyName.meaning}
					</div>

				{/* Read More Button */}
				<button
					onClick={() => {
						if (!expandedName && !nameReadLogged) {
							actions.toggleDeedComplete("asmaul_husna", `Reflecting on ${dailyName.transliteration}`);
							setNameReadLogged(true);
						}
						setExpandedName(!expandedName);
					}}
					className="mb-4 px-3 py-1.5 text-xs font-semibold text-mint-accent hover:text-mint-accent/80 transition-colors flex items-center gap-2"
				>
					{expandedName ? "Show Less" : "Read More"}
					<ChevronDown size={14} className={`transition-transform ${expandedName ? "rotate-180" : ""}`} />
				</button>

				{/* Expanded Meaning */}
				{expandedName && (
					<div className="w-full mb-4 p-3 rounded-lg bg-emerald-950/30 border border-emerald-900/30">
						<div className="text-sm text-zinc-300 leading-relaxed">
							{dailyName.detailedMeaning}
						</div>
					</div>
				)}

				{/* Reflection */}
				<div className="w-full">
					<label className="block text-xs text-zinc-600 uppercase tracking-widest font-semibold mb-2">Reflection</label>
					<textarea
						className="w-full rounded-lg border border-zinc-800 bg-[#09090B] text-zinc-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]"
						rows={2}
						placeholder={dailyName.reflection}
					/>
				</div>
			</div>
		</Card>

		{/* Quick Boosts */}
		<div>
			<div 
				className="flex items-center gap-2 mb-2 cursor-pointer hover:opacity-80 transition"
				onClick={() => setCollapsedBoosts(!collapsedBoosts)}
			>
				<Pill>Quick Boosts</Pill>
				<ChevronDown 
					size={18} 
					className={`text-zinc-500 transition-transform ${collapsedBoosts ? 'rotate-180' : ''}`}
				/>
			</div>
			{!collapsedBoosts && (
				<div className="space-y-2">
					{quickBoosts.map((boost) => {
						const IconComponent = boost.icon;
						return (
							<Card
								key={boost.id}
								className="cursor-pointer hover:border-mint-accent/50 transition"
								onClick={() => setExpandedBoost(expandedBoost === boost.id ? null : boost.id)}
							>
								<div className="flex items-center justify-between">
									<div>
										<div className="font-semibold text-white">{boost.title}</div>
										<div className="text-xs text-zinc-500 break-words">{boost.description}</div>
									</div>
									<div className={`flex-shrink-0 ${boost.iconColor}`}>
										<IconComponent size={20} />
									</div>
								</div>
								{expandedBoost === boost.id && (
									<div className="mt-3 text-sm text-zinc-300 space-y-2">
										<div>{boost.description}</div>
									</div>
								)}
							</Card>
						);
					})}
				</div>
			)}
			</div>

			{/* Knowledge Library */}
			<div>
				<div className="flex items-center gap-2 mb-3 bg-charcoal rounded-xl px-3 py-2 border border-zinc-800">
					<Search size={18} className="text-zinc-500" />
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search wisdom, dua, hadith…"
						className="flex-1 bg-transparent text-zinc-300 placeholder-zinc-600 outline-none text-sm"
					/>
				</div>
				<div className="space-y-4">
					{grouped.map((group) =>
						group.items.length === 0 ? null : (
							<div key={group.category}>
								<div 
									className="flex items-center gap-2 mb-2 cursor-pointer hover:opacity-80 transition"
									onClick={() => setCollapsedCategories(prev => ({
										...prev,
										[group.category]: !prev[group.category]
									}))}
								>
									<Pill>{group.category}</Pill>
									<ChevronDown 
										size={18} 
										className={`text-zinc-500 transition-transform ${collapsedCategories[group.category] ? 'rotate-180' : ''}`}
									/>
								</div>
								{!collapsedCategories[group.category] && (
									<div className="space-y-2">
										{group.items.map((item) => (
											<Card
												key={item.id}
												className="cursor-pointer hover:border-mint-accent/50 transition"
												onClick={() => setExpanded(expanded === item.id ? null : item.id)}
											>
												<div className="flex items-center justify-between">
													<div>
														<div className="font-semibold text-white">{item.title}</div>
														<div className="text-xs text-zinc-500 break-words">{item.content}</div>
													</div>
													<BookOpen size={18} className="text-mint-accent flex-shrink-0" />
												</div>
												{expanded === item.id && (
												<div className="mt-3 text-sm text-mint-accent/80 space-y-2">
														<div>{item.content}</div>
														{item.translation && (
															<div className="italic text-mint-accent/80">{item.translation}</div>
														)}
														{item.source && (
															<div className="text-xs text-zinc-500">Source: {item.source}</div>
														)}
														{item.tag && <Pill>{item.tag}</Pill>}
													</div>
												)}
											</Card>
										))}
									</div>
								)}
							</div>
						)
					)}
				</div>
			</div>
		</div>
	);
}